import React from 'react';
import { motion } from 'framer-motion';
import { Database, Tag, Layers, Eye } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
interface SubjectData {
  code: string;
  name: string;
  questionCount: number;
  familyCount: number;
  color: string;
  avgTier: number;
}
interface OverviewTabProps {
  subjects: SubjectData[];
  totalQuestions: number;
  totalFamilies: number;
  typeCount?: number;
  visualCount?: number;
  onBrowseSubject: (code: string) => void;
}
export function OverviewTab({
  subjects,
  totalQuestions,
  totalFamilies,
  typeCount = 0,
  visualCount = 0,
  onBrowseSubject
}: OverviewTabProps) {
  const activeSubjects = subjects.filter(s => s.questionCount > 0).length;
  return <div className="space-y-6" data-id="element-4964">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-4965">
        <motion.div variants={itemVariants} data-id="element-4966">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Database className="w-5 h-5" data-id="element-4968" />} color="blue" data-id="element-4967" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4969">
          <StatCard label="Families" value={totalFamilies} icon={<Tag className="w-5 h-5" data-id="element-4971" />} color="purple" data-id="element-4970" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4972">
          <StatCard label="Vraagtypen" value={typeCount} icon={<Layers className="w-5 h-5" data-id="element-4974" />} color="emerald" data-id="element-4973" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4975">
          <StatCard label="Actieve Vakken" value={`${activeSubjects} / ${subjects.length}`} icon={<Eye className="w-5 h-5" data-id="element-4977" />} color="amber" data-id="element-4976" />
        </motion.div>
      </motion.div>

      {/* Subject Cards Grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-4978">
        {subjects.map(subject => {
        const isActive = subject.questionCount > 0;
        return <motion.div key={subject.code} variants={itemVariants} data-id="element-4979">
              <Card className={`p-5 h-full flex flex-col transition-all duration-200 cursor-pointer border border-gray-200 ${isActive ? 'hover:shadow-md hover:border-blue-300' : 'opacity-50 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isActive && onBrowseSubject(subject.code)} data-id="element-4980">
                {/* Header */}
                <div className="flex items-start justify-between mb-4" data-id="element-4981">
                  <div className="flex items-center gap-3" data-id="element-4982">
                    <div className={`w-11 h-11 rounded-xl ${subject.color} flex items-center justify-center shadow-sm`} data-id="element-4983">
                      <span className="text-white font-black text-sm" data-id="element-4984">
                        {subject.code.slice(0, 2)}
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-4985">
                      <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-4986">
                        {subject.name}
                      </h3>
                      <code className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-4987">
                        {subject.code}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-2.5 mb-4 flex-1" data-id="element-4988">
                  <div className="flex items-center justify-between" data-id="element-4989">
                    <span className="text-xs text-gray-500" data-id="element-4990">Vragen</span>
                    <span className={`text-sm font-bold tabular-nums ${isActive ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-4991">
                      {subject.questionCount.toLocaleString('nl-NL')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-4992">
                    <span className="text-xs text-gray-500" data-id="element-4993">Families</span>
                    <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-4994">
                      {subject.familyCount}
                    </span>
                  </div>
                  {isActive && subject.avgTier > 0 && <div className="flex items-center justify-between" data-id="element-4995">
                      <span className="text-xs text-gray-500" data-id="element-4996">Gem. Tier</span>
                      <Badge variant="neutral" size="sm" data-id="element-4997">
                        {subject.avgTier}
                      </Badge>
                    </div>}
                </div>

                {/* Distribution bar */}
                {isActive && <div className="mb-4" data-id="element-4998">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4999">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${Math.min(100, subject.questionCount / Math.max(totalQuestions, 1) * 100)}%`
                }} transition={{
                  duration: 0.6,
                  delay: 0.1
                }} className={`h-full rounded-full ${subject.color}`} data-id="element-5000" />
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1 text-right" data-id="element-5001">
                      {Math.round(subject.questionCount / Math.max(totalQuestions, 1) * 100)}
                      % van totaal
                    </div>
                  </div>}

                {/* Action */}
                <div className="mt-auto pt-3 border-t border-gray-100" data-id="element-5002">
                  <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => isActive && onBrowseSubject(subject.code)} disabled={!isActive} data-id="element-5003">
                    <Eye className="w-4 h-4 mr-1.5" data-id="element-5004" />
                    Bekijk {subject.questionCount} vragen
                  </Button>
                </div>
              </Card>
            </motion.div>;
      })}
      </motion.div>
    </div>;
}